import { Observable, of, defer, EMPTY, merge } from "rxjs"
import { switchMap, map, catchError, filter, take, delayWhen } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { compact } from "lodash"
import moment from "moment-timezone"
import { Router } from "../../../../../server/router"
import type { EpicDependencies } from "../../../../rxjs/configureEpics"
import {
    FETCH_CUSTOMER_DETAIL_SUCCESS,
    FETCH_CUSTOMER_LOYALTY_STATS_REQUESTED,
    FETCH_CUSTOMER_ORDERS_REQUESTED,
    FETCH_CUSTOMERS_REPORT_REQUESTED,
    FETCH_LOYALTY_PROGRAM_SUCCESS,
    fetchCustomerDetailRequested,
    fetchCustomerLoyaltyStatsRequested,
    fetchCustomerLoyaltyStatsSuccess,
    fetchCustomerOrdersFailure,
    fetchCustomerOrdersRequested,
    fetchCustomerOrdersSuccess,
    fetchCustomersReportRequested,
    fetchCustomersReportSuccess,
    fetchOrdersReportFailure,
} from "../../../../../api/actions"
import { getAccounts, getHttpHeaders, getLocationsAuthData, getSelectedAccountId } from "../../../../auth/selectors"
import {
    composeCustomer,
    mapCustomerAddressToUpdateRequestedPayload,
    mapCustomerLoyaltyToCreateRequestedPayload,
    mapCustomerToUpdateRequestedPayload,
    transformCustomerOrdersServerData,
} from "./utils"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../../auth/actions"
import {
    clearCustomersData,
    CLOSE_CUSTOMER_FORM_CLICKED,
    CUSTOMER_AGENDA_MOUNTED,
    CUSTOMER_FORM_SUBMITTED,
    customerAgendaMounted,
    DELETE_CUSTOMER_CLICKED,
    EDIT_CUSTOMER_OPENED,
    ENROLL_TO_LOYALTY_CLICKED,
    REMOVE_LOYALTY_CLICKED,
} from "../actions"
import {
    createActionLoadingError,
    CUSTOMER_DELETED,
    CUSTOMER_LOYALTY_PROGRAM_CREATED,
    CUSTOMER_LOYALTY_PROGRAM_UPDATED,
    CUSTOMER_UPDATED,
    customerDeleted,
    customerLoyaltyProgramCreated,
    customerLoyaltyProgramUpdated,
    customerUpdated,
} from "../../../../data/actions"
import {
    createLoadingEnded,
    createLoadingStarted,
    SET_CUSTOMERS_REPORT_DEFAULT_FILTER,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_GLOBAL_DEFAULT_FILTER,
    SET_PAGINATION,
    setCustomersReportDefaultFilter,
    setPagination,
    setTotalCount,
} from "../../../../filters/actions"
import { getCustomers, getLoyaltyProgramFormData } from "../../../../data/selectors"
import type { Action, StateObject } from "../../../../redux/types"
import {
    getCustomersByPageNumber,
    getCustomersCurrentPage,
    getCustomersQueryApiParam,
    isCustomerReportAgendaOpened,
} from "./selectors"
import { getSelectedLocationIdsFromGlobalFilter } from "../../../../filters/selectors"
import type { Customer, CustomerAddress, CustomerLoyaltyProgram } from "../../../../data/types"
import { fetchCustomersReportData } from "../request"
import { prepareQueryApiParameters } from "../../../../../utils/queryApi/queryApiRequest"
import { isLoyaltyDataFetched } from "../../../../data/utils"
import { LOAD_ACCOUNTS_SUCCESS, LOAD_AUTH_LOCATIONS_SUCCESS } from "../../../../layouts/header/actions"
import { createEventRequest, requestEvent } from "../../../../../events/utils"
import { EventType } from "../../../../../events/types"
import { logger } from "../../../../../utils/loggerWrap"
import { NotificationType, openNotification } from "../../../../uiKit/Notification/Notification"

export const customerAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CUSTOMER_AGENDA_MOUNTED),
        delayWhen((action: Action) => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => {
            return getSelectedAccountId(deps.getState()) ? [setCustomersReportDefaultFilter()] : []
        })
    )

export const fetchCustomersReportOnAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isCustomerReportAgendaOpened(deps.getState())),
        switchMap(() => [customerAgendaMounted()])
    )

export const nextCustomerPageRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_PAGINATION),
        filter((action: Action) => action.payload.identifier === "customers"),
        switchMap(action => {
            const pageToGo = action.payload.paginationValue.currentPage

            if (!pageToGo) {
                return []
            }

            const pageData = getCustomersByPageNumber(deps.getState(), pageToGo)
            return !pageData || pageData.length === 0 ? [fetchCustomersReportRequested()] : []
        })
    )

export const customerFilterChangedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(SET_CUSTOMERS_REPORT_DEFAULT_FILTER)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter((action: Action) => action.payload.identifier === "customers")
        )
    ).pipe(
        switchMap(() => {
            return [
                clearCustomersData(),
                setPagination("customers", {
                    currentPage: 1,
                }),
            ]
        })
    )

export const customersReportLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CUSTOMERS_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isCustomerReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("customers")])
    )

export const fetchCustomersReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CUSTOMERS_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isCustomerReportAgendaOpened(deps.getState())),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() => {
            return defer(() =>
                fetchCustomersReportData(
                    getHttpHeaders(deps.getState()),
                    prepareQueryApiParameters(getCustomersQueryApiParam(deps.getState()))
                )
            ).pipe(
                switchMap(result => {
                    if (result && result.data) {
                        return [
                            createLoadingEnded("customers"),
                            setTotalCount("customers", result.totalCount),
                            fetchCustomersReportSuccess(
                                compact(result.data || []),
                                getCustomersCurrentPage(deps.getState())
                            ),
                        ]
                    }

                    return [fetchOrdersReportFailure(), createLoadingEnded("customers")]
                }),
                catchError(error => of(createActionLoadingError(error)))
            )
        })
    )

export const fetchCustomerOrdersRequestedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CUSTOMER_ORDERS_REQUESTED),
        switchMap((action: Action) =>
            deps.api.getCustomerOrdersApi(getHttpHeaders(deps.getState()), action.payload.customerId).pipe(
                switchMap(response => [
                    createLoadingEnded("customer-orders"),
                    createLoadingEnded("customer-detail"),
                    fetchCustomerOrdersSuccess(transformCustomerOrdersServerData(response)),
                ]),
                catchError(error => of(fetchCustomerOrdersFailure(error)))
            )
        )
    )

export const fetchCustomerDetailData = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(EDIT_CUSTOMER_OPENED),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        delayWhen(action => waitForLoyaltyFetched(actions$, deps.getState(), action)),
        filter(action => action.payload.customerId),
        switchMap(action => {
            return [
                createLoadingStarted("customer-detail"),
                createLoadingStarted("customer-orders"),
                fetchCustomerDetailRequested(action.payload.customerId),
                fetchCustomerOrdersRequested(action.payload.customerId),
            ]
        })
    )

export const customerFormSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CUSTOMER_FORM_SUBMITTED),
        switchMap((action: Action) => {
            const allCustomers = getCustomers(deps.getState())
            const originCustomer = allCustomers.find(c => c.id === action.payload.newValue.customerId)

            if (!action.payload.oldValue || !originCustomer) {
                return []
            } else {
                return [
                    customerUpdated(
                        composeCustomer(action.payload.newValue, originCustomer),
                        null,
                        getCustomersCurrentPage(deps.getState())
                    ),
                ]
            }
        })
    )

export const customerSubmittedRouterEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CUSTOMER_FORM_SUBMITTED, CLOSE_CUSTOMER_FORM_CLICKED, DELETE_CUSTOMER_CLICKED),
        switchMap(() => {
            Router.push("/reports/customers")
            return EMPTY
        })
    )

export const deleteCustomerRequestedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_CUSTOMER_CLICKED),
        switchMap((action: Action) => {
            const customer = getCustomers(deps.getState()).find(c => c.id === action.payload.customerId)
            return customer ? [customerDeleted(customer, getCustomersCurrentPage(deps.getState()))] : []
        })
    )

export const deleteCustomerEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CUSTOMER_DELETED),
        switchMap((action: Action) =>
            defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.CustomerDeleteRequested,
                        {
                            correlationId: deps.idgen.next(),
                            accountId: getSelectedAccountId(deps.getState()) as string,
                        },
                        {
                            id: action.payload.customer.id,
                        }
                    )
                )
            ).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Customer deleted")
                    return []
                }),
                catchError(err => {
                    logger.error("deleteCustomerEpic - event failed", err)
                    openNotification(NotificationType.Error, "Customer Delete Failed", err?.message)
                    return []
                })
            )
        )
    )

export const customerDetailFetchedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CUSTOMER_DETAIL_SUCCESS),
        switchMap((action: Action) => {
            if (action.payload.data.length > 0 && action.payload.data[0].customerLoyaltyProgram) {
                return [
                    createLoadingStarted("customer-loyalty-program"),
                    fetchCustomerLoyaltyStatsRequested(
                        action.payload.data[0].customerLoyaltyProgram.id,
                        action.payload.data[0].id
                    ),
                ]
            }

            return []
        })
    )

export const fetchCustomerLoyaltyProgramRequestedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CUSTOMER_LOYALTY_STATS_REQUESTED),
        switchMap((action: Action) =>
            deps.api
                .getCustomerLoyaltyProgramStatsApi(getHttpHeaders(deps.getState()), action.payload.loyaltyProgramId)
                .pipe(
                    switchMap(response => {
                        return [
                            createLoadingEnded("customer-loyalty-program"),
                            fetchCustomerLoyaltyStatsSuccess({
                                ...response,
                                customerLoyaltyProgramId: action.payload.loyaltyProgramId,
                                customerId: action.payload.customerId,
                            }),
                        ]
                    }),
                    catchError(error => of(fetchCustomerLoyaltyStatsSuccess(error)))
                )
        )
    )

export const enrollToLoyaltyEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ENROLL_TO_LOYALTY_CLICKED),
        switchMap((action: Action) => {
            const customerLoyalty: Partial<CustomerLoyaltyProgram> = {
                id: deps.idgen.next(),
                customer_id: action.payload.customerId,
                loyalty_program_id: action.payload.loyaltyId,
                opt_in_date: moment().utc().format(),
                opt_out_date: null,
                stats: null,
                origin_timestamp: moment().utc().format(),
            }

            return [customerLoyaltyProgramCreated(customerLoyalty as CustomerLoyaltyProgram)]
        })
    )

export const removeCustomerLoyaltyEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(REMOVE_LOYALTY_CLICKED),
        switchMap((action: Action) => {
            const newVal = {
                customer_id: action.payload.customerId,
                id: action.payload.customerLoyaltyId,
                opt_out_date: moment().utc().format(),
            }
            return [customerLoyaltyProgramUpdated(newVal)]
        })
    )
export const customerUpdatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CUSTOMER_UPDATED),
        switchMap((action: Action) => {
            const customer: Customer = action.payload.newValue
            const headers = getHttpHeaders(deps.getState())
            const accountId = getSelectedAccountId(deps.getState())

            return defer(() =>
                Promise.all([
                    requestEvent(
                        headers,
                        createEventRequest(
                            EventType.CustomerUpdateRequested,
                            {
                                correlationId: deps.idgen.next(),
                                accountId: accountId as string,
                            },
                            mapCustomerToUpdateRequestedPayload(customer)
                        )
                    ),
                    ...customer.addresses.map((a: CustomerAddress) =>
                        requestEvent(
                            headers,
                            createEventRequest(
                                EventType.CustomerAddressUpdateRequested,
                                {
                                    correlationId: deps.idgen.next(),
                                    accountId: accountId as string,
                                },
                                mapCustomerAddressToUpdateRequestedPayload(a)
                            )
                        )
                    ),
                ])
            ).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Customer updated")
                    return []
                }),
                catchError(err => {
                    logger.error("customerUpdatedEpic - event failed", err)
                    openNotification(NotificationType.Error, "Customer Update Failed", err?.message)
                    return []
                })
            )
        })
    )

export const customerLoyaltyProgramCreatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CUSTOMER_LOYALTY_PROGRAM_CREATED),
        switchMap((action: Action) => {
            const customerLoyalty: CustomerLoyaltyProgram = action.payload.customerLoyaltyProgram

            return defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.CustomerLoyaltyProgramCreateRequested,
                        {
                            correlationId: deps.idgen.next(),
                            accountId: getSelectedAccountId(deps.getState()) as string,
                        },
                        mapCustomerLoyaltyToCreateRequestedPayload(customerLoyalty)
                    )
                )
            ).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Customer enrolled to loyalty program")
                    return []
                }),
                catchError(err => {
                    logger.error("customerLoyaltyProgramCreatedEpic - event failed", err)
                    openNotification(NotificationType.Error, "Loyalty Program Enrollment Failed", err?.message)
                    return []
                })
            )
        })
    )

export const customerLoyaltyProgramRemovedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CUSTOMER_LOYALTY_PROGRAM_UPDATED),
        filter((action: Action) => !!action.payload.newValue.opt_out_date),
        switchMap((action: Action) =>
            defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.CustomerLoyaltyProgramRemoveRequested,
                        {
                            correlationId: deps.idgen.next(),
                            accountId: getSelectedAccountId(deps.getState()) as string,
                        },
                        {
                            id: action.payload.newValue.id,
                        }
                    )
                )
            ).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Customer removed from loyalty program")
                    return []
                }),
                catchError(err => {
                    logger.error("customerLoyaltyProgramRemovedEpic - event failed", err)
                    openNotification(NotificationType.Error, "Loyalty Program Removal Failed", err?.message)
                    return []
                })
            )
        )
    )

// Todo: move somewhere

export const waitForAccountSelected = (actions$: Observable<Action>, state: StateObject, action: any) =>
    getSelectedAccountId(state)
        ? of(action)
        : actions$.pipe(
              ofType(ACCOUNT_SELECTED),
              take(1),
              map(() => action)
          )

export const waitForAccountsLoaded = (actions$: Observable<Action>, state: StateObject, action: any) =>
    (getAccounts(state) ?? []).length > 0
        ? of(action)
        : actions$.pipe(
              ofType(LOAD_ACCOUNTS_SUCCESS),
              take(1),
              map(() => action)
          )

export const waitForLocationsLoaded = (actions$: Observable<Action>, state: StateObject, action: any) =>
    getLocationsAuthData(state).length > 0
        ? of(action)
        : actions$.pipe(
              ofType(LOAD_AUTH_LOCATIONS_SUCCESS),
              take(1),
              map(() => action)
          )

export const waitForGlobalFilterSet = (actions$: Observable<Action>, state: StateObject, action: any) => {
    return getSelectedLocationIdsFromGlobalFilter(state).length > 0
        ? of(action)
        : actions$.pipe(
              ofType(SET_GLOBAL_DEFAULT_FILTER),
              take(1),
              map(() => action)
          )
}

export const waitForLoyaltyFetched = (actions$: Observable<Action>, state: StateObject, action: any) => {
    return isLoyaltyDataFetched(getLoyaltyProgramFormData(state))
        ? of(action)
        : actions$.pipe(
              ofType(FETCH_LOYALTY_PROGRAM_SUCCESS),
              take(1),
              map(() => action)
          )
}

export default [
    customerUpdatedEpic,
    customerLoyaltyProgramCreatedEpic,
    customerLoyaltyProgramRemovedEpic,
    removeCustomerLoyaltyEpics,
    enrollToLoyaltyEpics,
    customerDetailFetchedEpic,
    fetchCustomerLoyaltyProgramRequestedEpic,
    customersReportLoadingStartedEpics,
    customerAgendaMountedEpics,
    fetchCustomerOrdersRequestedEpic,
    fetchCustomersReportRequestedEpics,
    customerSubmittedRouterEpics,
    fetchCustomersReportOnAccountSwitch,
    fetchCustomerDetailData,
    customerFormSubmittedEpics,
    nextCustomerPageRequestedEpics,
    customerFilterChangedEpics,
    deleteCustomerRequestedEpic,
    deleteCustomerEpic,
]
